import { useCallback } from "react";
import Swal from "sweetalert2";
import {
    finalizeDesignerArtifacts,
    getMasterTemplateForOrderItem,
    getPrintsDetails,
    saveMasterTemplate,
    submitDesignerBundle,
} from "../../services/api";
import { adaptPrintDetailsResponse } from "../../utils/designerAdapters";
import { validatePayloadShape } from "../../utils/designerState";
import { resolveSafeTextMaxLetters } from "../../utils/designerPrintStrategies";
import {
    applyPredesignSizeAttrs,
    buildPrintOccupancyKey,
    isFixedPredesignSelection,
    roundUpToHalfCm,
    resolvePrintSelectionSide,
} from "../../utils/designerHelpers";
import enrichPrintAttrsForSave from "../../utils/designerPrintSaveEnrichment";
import { renderSketchImage } from "../../utils/sketchRenderer";
import { buildDesignerGuidePdfDataUrl } from "./designerGuidePdf";

const useDesignerSaveFlow = ({
    orderItemId,
    observations = "",
    resultTreeArray,
    blockingIssues,
    printDetails,
    selectedPrints,
    appliedPartsPreview,
    bSide,
    customerId,
    productModelId,
    draftStorageKey,
    datasheetGuard,
    saveBlockedByExistingDatasheet,
    setDatasheetGuard,
    setSaving,
    setSaveMsg,
    normalizedRoot,
    selectedPartsForPreview,
    allColors,
    stripeDrawingsBySide = null,
    stripeStrokeSummary = null,
    customCutsBySide = null,
    customCutZoneColors = null,
    customCutZonesBySide = null,
    customCutRuntimeBySideRef = null,
    customCutSummary = null,
    setSketchImage,
    setSketchError,
    onBundleUploaded,
    onSaveSuccess,
    appliedPartsBySection = [],
    appliedPrintsBySection = [],
    garmentName = "Tu prenda",
}) => useCallback(async () => {
    const normalizeRoundedPrintDimension = (rawValue) => {
        const parsed = Number(rawValue);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return roundUpToHalfCm(parsed).toFixed(1);
    };

    const normalizeRoundedPrintAttrs = (attrs = {}) => {
        const nextAttrs = { ...attrs };
        [
            "width",
            "height",
            "rendered_width_cm",
            "rendered_height_cm",
            "real_width_cm",
            "real_height_cm",
        ].forEach((key) => {
            const normalized = normalizeRoundedPrintDimension(nextAttrs[key]);
            if (normalized !== null) {
                nextAttrs[key] = normalized;
            }
        });
        return nextAttrs;
    };

    if (!orderItemId) {
        setSaveMsg("Falta order_item_id para guardar.");
        return;
    }
    if (!resultTreeArray.length) {
        setSaveMsg("No hay selección para guardar.");
        return;
    }
    if (blockingIssues.length > 0) {
        setSaveMsg(blockingIssues[0].message);
        return;
    }
    if (saveBlockedByExistingDatasheet) {
        setSaveMsg(datasheetGuard.message || "No se puede guardar una nueva ficha hasta que todas las anteriores estén rechazadas.");
        return;
    }

    try {
        const guardResponse = await getMasterTemplateForOrderItem(orderItemId, bSide);
        const canCreate = guardResponse?.can_create !== undefined
            ? !!guardResponse.can_create
            : !guardResponse?.exists;
        if (!canCreate) {
            setDatasheetGuard({
                loading: false,
                canCreate: false,
                message: "Ya existe una ficha activa para esta prenda. Podrás guardar una nueva cuando Little Blue la revise.",
            });
            setSaveMsg("No se puede guardar una nueva ficha hasta que todas las anteriores estén rechazadas.");
            return;
        }
    } catch (error) {
        setSaveMsg("No se pudo validar el estado de fichas existentes. Reintentá en unos segundos.");
        return;
    }

    const confirmResult = await Swal.fire({
        icon: "question",
        title: "Confirmar guía de diseño",
        text: "¿Seguro que querés confirmar la guía?",
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#0071ce",
        cancelButtonColor: "#d54ab7",
        reverseButtons: true,
    });
    if (!confirmResult.isConfirmed) {
        return;
    }

    setSaving(true);
    setSaveMsg(null);

    let detailMap = { ...printDetails };
    try {
        const printIds = Object.values(selectedPrints || {})
            .map((selection) => selection?.print?.id)
            .filter(Boolean);
        if (printIds.length > 0) {
            const response = await getPrintsDetails(printIds);
            adaptPrintDetailsResponse(response).forEach((print) => {
                detailMap[print.id] = print;
            });
        }
    } catch (error) {
        console.warn("No se pudieron cargar detalles de print para guardado", error);
    }

    const appliedPartsPayload = appliedPartsPreview.map((row) => {
        const attrs = {};
        Object.entries(row.attrs || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return;
            if (String(key) === "personal") {
                const raw = typeof value === "string" ? value.trim().toLowerCase() : value;
                const isPersonal =
                    raw === true
                    || raw === 1
                    || raw === "1"
                    || raw === "true";
                attrs.personal = isPersonal ? "1" : "0";
                return;
            }
            attrs[String(key)] = String(value);
        });

        if (row.master_print_id && detailMap[row.master_print_id]) {
            const details = detailMap[row.master_print_id];
            if (!attrs.height) {
                const fallbackHeight = details.max_height ?? details.min_height ?? null;
                if (fallbackHeight) attrs.height = String(fallbackHeight);
            }
            const maxLetters = resolveSafeTextMaxLetters({
                selection: { print: row, attrs },
                details,
            });
            if (attrs.personal === "1") {
                attrs.text = "Apodo";
            }
            if (maxLetters > 0 && attrs.text) {
                attrs.text = attrs.text.slice(0, maxLetters);
            }
        }

        // free_scale=1 → el cliente lo agrandó a mano: NO pisar con el tamaño fijo del tier.
        if (String(attrs?.free_scale || "") !== "1"
            && isFixedPredesignSelection({ print: detailMap[row.master_print_id] || row, attrs, mode: attrs?.print_kind === "predesign" ? "predesign" : "image" })) {
            Object.assign(attrs, applyPredesignSizeAttrs(attrs, attrs?.predesign_size_tier, detailMap[row.master_print_id] || row));
        }

        const normalizedAttrs = normalizeRoundedPrintAttrs(attrs);
        const enrichedAttrs = enrichPrintAttrsForSave({
            row,
            attrs: normalizedAttrs,
            details: detailMap[row.master_print_id] || row,
        });

        return {
            ...row,
            attrs: enrichedAttrs,
        };
    });

    const payload = {
        order_item_id: orderItemId,
        b: bSide,
        tree: resultTreeArray,
        applied_parts: appliedPartsPayload,
        designer_meta: {
            stripe_drawings: {
                version: 1,
                front: Array.isArray(stripeDrawingsBySide?.front) ? stripeDrawingsBySide.front : [],
                back: Array.isArray(stripeDrawingsBySide?.back) ? stripeDrawingsBySide.back : [],
            },
            custom_cuts: {
                version: 1,
                front: Array.isArray(customCutsBySide?.front) ? customCutsBySide.front : [],
                back: Array.isArray(customCutsBySide?.back) ? customCutsBySide.back : [],
                zone_colors: customCutZoneColors && typeof customCutZoneColors === "object" ? customCutZoneColors : {},
            },
        },
        product_model_id: productModelId || undefined,
        customer_id: customerId,
        // Observaciones del cliente → V4 store() las guarda en datasheets.notes (las ve Diseño).
        comments: (observations || "").trim() || undefined,
    };

    const invalidPrintRow = appliedPartsPayload.find((row) => {
        const attrs = row?.attrs || {};
        const looksLikePrintRow =
            attrs.location_key !== undefined
            || attrs.resolved_technique !== undefined
            || attrs.text !== undefined
            || attrs.image_url !== undefined;
        return looksLikePrintRow && !Number(row?.master_print_id);
    });
    if (invalidPrintRow) {
        setSaving(false);
        setSaveMsg("No se pudo resolver un tipo de bordado/aplique válido para guardar. Reintentá agregando nuevamente ese bordado.");
        return;
    }

    const printLocationSeen = new Set();
    const duplicatePrintRow = appliedPartsPayload.find((row) => {
        const attrs = row?.attrs || {};
        const locationKey = String(attrs.location_key || "").trim();
        if (!locationKey || !row?.master_print_id) return false;
        const side = resolvePrintSelectionSide({ attrs }, "front") || "front";
        const occupancyKey = buildPrintOccupancyKey(locationKey, side);
        if (printLocationSeen.has(occupancyKey)) return true;
        printLocationSeen.add(occupancyKey);
        return false;
    });
    if (duplicatePrintRow) {
        setSaving(false);
        setSaveMsg("Hay dos diseños ocupando la misma ubicación en el mismo lado. Reubicá uno de ellos antes de guardar.");
        return;
    }

    const payloadValidation = validatePayloadShape(payload);
    if (!payloadValidation.ok) {
        setSaving(false);
        setSaveMsg(payloadValidation.error);
        return;
    }

    try {
        const saveResponse = await saveMasterTemplate(payload);
        let generatedSketch = null;
        try {
            generatedSketch = await renderSketchImage({
                selectedParts: selectedPartsForPreview,
                root: normalizedRoot,
                selectedPrints,
                allColors,
                stripeDrawingsBySide,
                customCutsBySide,
                customCutZoneColors,
                customCutZonesBySide,
                customCutRuntimeBySide: customCutRuntimeBySideRef?.current || null,
                layout: "vertical",
            });
            if (generatedSketch) {
                setSketchImage?.(generatedSketch);
                setSketchError?.(null);
            }
        } catch (error) {
            setSketchError?.("No se pudo generar el boceto automáticamente.");
        }

        try {
            let guidePdfDataUrl = null;
            try {
                guidePdfDataUrl = await buildDesignerGuidePdfDataUrl({
                    orderItemId,
                    garmentName,
                    appliedPartsBySection,
                    selectedTree: resultTreeArray,
                    normalizedRoot,
                    appliedPartsPreview,
                    appliedPrintsBySection,
                    selectedPrints,
                    allColors,
                    stripeStrokeSummary,
                    customCutSummary,
                    sketchDataUrl: generatedSketch,
                });
            } catch (pdfError) {
                console.warn("No se pudo generar el PDF de resumen en front, se usará fallback del backend.", pdfError);
            }

            const bundleResponse = await submitDesignerBundle(orderItemId, {
                sketch_data_url: generatedSketch,
                guide_pdf_data_url: guidePdfDataUrl,
                datasheet_id: saveResponse?.id || null,
                customer_id: customerId || null,
                summary: {
                    parts: appliedPartsBySection,
                    customizations: appliedPrintsBySection,
                    stripes: {
                        total_strokes: Number(stripeStrokeSummary?.total || 0),
                        front_strokes: Number(stripeStrokeSummary?.front || 0),
                        back_strokes: Number(stripeStrokeSummary?.back || 0),
                        straight_strokes: Number(stripeStrokeSummary?.straight || 0),
                        serpentina_strokes: Number(stripeStrokeSummary?.curved || 0),
                    },
                    custom_cuts: {
                        total_cuts: Number(customCutSummary?.total || 0),
                        front_cuts: Number(customCutSummary?.front || 0),
                        back_cuts: Number(customCutSummary?.back || 0),
                        cuts: Array.isArray(customCutSummary?.details)
                            ? customCutSummary.details.map((detail) => ({
                                id: String(detail?.id || ""),
                                label: String(detail?.label || "Recorte"),
                                colors: Array.isArray(detail?.colors)
                                    ? detail.colors.map((color) => ({
                                        id: color?.id ?? color?.color_id ?? null,
                                        color_id: color?.color_id ?? color?.id ?? null,
                                        code: String(color?.code || "").trim(),
                                        name: String(color?.name || color?.code || "Color").trim(),
                                    }))
                                    : [],
                            }))
                            : [],
                    },
                },
                selected_prints: selectedPrints,
                stripe_drawings: stripeDrawingsBySide || null,
                custom_cuts: customCutsBySide || null,
                custom_cut_zone_colors: customCutZoneColors || null,
            });
            onBundleUploaded?.(bundleResponse);
            const guideOrderItemFileId = bundleResponse?.guide?.id || null;
            const sketchOrderItemFileId = bundleResponse?.sketch?.id || null;
            if (guideOrderItemFileId || sketchOrderItemFileId) {
                await finalizeDesignerArtifacts({
                    order_item_id: orderItemId,
                    datasheet_id: saveResponse?.id || null,
                    guide_order_item_file_id: guideOrderItemFileId,
                    sketch_order_item_file_id: sketchOrderItemFileId,
                });
            }
        } catch (bundleError) {
            setSaveMsg("La ficha se guardó, pero hubo un problema subiendo boceto/resumen o avisando al equipo. Reintentá.");
        }

        if (typeof window !== "undefined") {
            window.localStorage.removeItem(draftStorageKey);
            window.localStorage.removeItem(`${draftStorageKey}:stripes`);
        }
        setDatasheetGuard({
            loading: false,
            canCreate: false,
            message: "La ficha ya fue guardada y quedó pendiente de aprobación por parte de Little Blue.",
        });
        setSaveMsg("Guardado correcto.");
        await Swal.fire({
            icon: "success",
            title: "¡Guardado con éxito!",
            html: "Tu ficha quedó pendiente de aprobación por parte de <strong>Little Blue</strong>.",
            showCloseButton: true,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#0071ce",
        });
        onSaveSuccess?.();
    } catch (error) {
        setSaveMsg(`Error al guardar: ${error.message}`);
    } finally {
        setSaving(false);
    }
}, [
    orderItemId,
    observations,
    resultTreeArray,
    blockingIssues,
    printDetails,
    selectedPrints,
    appliedPartsPreview,
    appliedPartsBySection,
    appliedPrintsBySection,
    bSide,
    customerId,
    productModelId,
    draftStorageKey,
    datasheetGuard,
    saveBlockedByExistingDatasheet,
    setDatasheetGuard,
    setSaving,
    setSaveMsg,
    normalizedRoot,
    selectedPartsForPreview,
    allColors,
    stripeDrawingsBySide,
    stripeStrokeSummary,
    customCutsBySide,
    customCutZoneColors,
    customCutZonesBySide,
    customCutRuntimeBySideRef,
    customCutSummary,
    setSketchImage,
    setSketchError,
    onBundleUploaded,
    onSaveSuccess,
    garmentName,
]);

export default useDesignerSaveFlow;
